import {Box, Button, Stack, Text, Title, createStyles} from '@mantine/core';
import {css} from 'aphrodite';
import React, {useCallback} from 'react';

import {getExtensionIcon} from 'dev-platform/extensionIconMapper';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import Modal from 'ui-library/modals/Modal';

import {extensionIconStyles} from './ExtensionDetails';

const useStyles = createStyles((theme) => ({
  iconWrap: {
    backgroundColor: theme.colors.teal[0],
    borderRadius: theme.radius.sm,
  },
  primaryButton: {
    width: '100%',
  },
}));

function MCPSetupRedirectPane({
  extensionId,
  extensionType,
  displayName,
  onGoToAdminSettings,
}: {
  extensionId: string;
  extensionType: ExtensionType;
  displayName: string;
  onGoToAdminSettings: () => boolean;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const handleGoToAdmin = useCallback(() => {
    onGoToAdminSettings();
  }, [onGoToAdminSettings]);

  return (
    <Stack spacing="xl" px="md" pt={32} pb={120} align="center">
      <Box className={cx(css(extensionIconStyles.icon), classes.iconWrap)}>
        {getExtensionIcon(extensionId, {
          className: css(extensionIconStyles.iconImg, extensionIconStyles.iconPlaceholder),
          extensionType,
        })}
      </Box>
      <Stack spacing="sm" align="center">
        <Title order={3} ta="center">
          {displayName} is installed
        </Title>
        <Text c="dimmed" size="sm" ta="center" maw={440}>
          This MCP is installed, but you need to finish setting it up. Open admin settings to connect and
          grant tool access.
        </Text>
      </Stack>
      <Stack spacing="md" w="100%" maw={440}>
        <Button className={classes.primaryButton} size="md" onClick={handleGoToAdmin}>
          Go to Admin Settings →
        </Button>
      </Stack>
    </Stack>
  );
}

interface MCPSetupRedirectModalProps {
  extensionId: string;
  displayName: string;
  onGoToAdminSettings: () => boolean;
  onRequestClose: () => void;
}

export default function MCPSetupRedirectModal({
  extensionId,
  displayName,
  onGoToAdminSettings,
  onRequestClose,
}: MCPSetupRedirectModalProps): JSX.Element {
  const handleGoToAdminSettings = useCallback(() => {
    const didNavigate = onGoToAdminSettings();
    if (didNavigate) {
      onRequestClose();
    }
    return didNavigate;
  }, [onGoToAdminSettings, onRequestClose]);

  return (
    <Modal
      title={null}
      onRequestClose={onRequestClose}
      bodyPadded={false}
      showHeaderBorder={false}
      closeableOnBackgroundClick={true}
    >
      <MCPSetupRedirectPane
        extensionId={extensionId}
        extensionType={ExtensionType.MCP_SERVER}
        displayName={displayName}
        onGoToAdminSettings={handleGoToAdminSettings}
      />
    </Modal>
  );
}
